/** Approval queue — admin/manager/owner approve/deny viewer requests
 * (Prompt 11E.5, build item 3). A 'staff' (teacher) caller sees only
 * reschedule requests for their own assigned students (server-scoped —
 * see backend/app/features/requests/router.py::list_requests).
 *
 * Approving/denying here does NOT itself perform the underlying mutation
 * (reschedule/cancel/unenroll) — that remains a deliberate manual admin
 * action via the existing flows (Calendar, Memberships, Students). This
 * queue is a pure request/decision audit trail.
 */

import React, { useState } from 'react';
import {
  useApproveRequest,
  useDenyRequest,
  useRequestQueue,
  type RequestRecord,
} from '@/features/parent-portal/api/requests';

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '1rem 1.25rem',
  marginBottom: '0.75rem',
};

const RequestRow: React.FC<{ request: RequestRecord }> = ({ request }) => {
  const [note, setNote] = useState('');
  const approve = useApproveRequest();
  const deny = useDenyRequest();
  const isPending = request.status === 'pending';
  const busy = approve.isPending || deny.isPending;

  return (
    <div style={card} data-testid={`candeza-approval-queue-row-${request.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong style={{ textTransform: 'capitalize' }}>{request.type.replace('_', ' ')}</strong>
          <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
            Student: {request.student_id}
            {request.session_id && ` · Session: ${request.session_id}`}
            {request.proposed_start && ` · Proposed: ${request.proposed_start}`}
            {request.membership_id && ` · Membership: ${request.membership_id}`}
          </div>
          {request.note && (
            <div style={{ fontSize: '0.8125rem', color: '#374151', marginTop: 4 }}>
              &ldquo;{request.note}&rdquo;
            </div>
          )}
        </div>
        <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{request.status}</span>
      </div>

      {isPending && (
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid={`candeza-approval-queue-note-${request.id}`}
            style={{ flex: 1 }}
          />
          <button
            disabled={busy}
            onClick={() => approve.mutate({ id: request.id, body: { note: note || undefined } })}
            data-testid={`candeza-approval-queue-approve-${request.id}`}
          >
            Approve
          </button>
          <button
            disabled={busy}
            onClick={() => deny.mutate({ id: request.id, body: { note: note || undefined } })}
            data-testid={`candeza-approval-queue-deny-${request.id}`}
          >
            Deny
          </button>
        </div>
      )}
    </div>
  );
};

const ApprovalQueuePage: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'denied' | ''>(
    'pending',
  );
  const { data, isLoading, isError } = useRequestQueue(statusFilter || undefined);
  const requests = data?.items ?? [];

  return (
    <div data-testid="candeza-approval-queue-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem' }}>
        Requests — Approval Queue
      </h1>

      <div style={{ marginBottom: '1rem' }}>
        {(['pending', 'approved', 'denied', ''] as const).map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            data-testid={`candeza-approval-queue-filter-${s || 'all'}`}
            style={{
              marginRight: '0.5rem',
              fontWeight: statusFilter === s ? 700 : 400,
            }}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div data-testid="candeza-approval-queue-loading">Loading…</div>
      ) : isError ? (
        <div data-testid="candeza-approval-queue-error" style={{ color: '#ef4444' }}>
          Could not load the approval queue.
        </div>
      ) : requests.length === 0 ? (
        <p data-testid="candeza-approval-queue-empty" style={{ color: '#6b7280' }}>
          No requests here.
        </p>
      ) : (
        <div data-testid="candeza-approval-queue-list">
          {requests.map((r) => (
            <RequestRow key={r.id} request={r} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalQueuePage;

